// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { Body, InputDate, InputText, LgSpacing, XlPadding } from '@alto/design-system';
import React from 'react';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';
import {
  type VacationSupplyFormValues,
  initialFormValues,
  validateReturnDate,
  validateStartDate,
} from '~shared/features/vacation-supply/helpers';

type VacationSupplyFormFieldsProps = {
  readonly control: Control<VacationSupplyFormValues>;
  readonly description?: string;
  readonly earliestAvailableDate: string;
  readonly initialValues?: VacationSupplyFormValues;
};

/**
 * Shared vacation supply fields (destination + start date + return date).
 * Used by VacationOverrideForm (native) and web Vacation Supply action sheets.
 * Uses InputDate for start/return date on both web and native.
 */
export const VacationSupplyFormFields = ({
  control,
  description,
  earliestAvailableDate,
  initialValues = initialFormValues,
}: VacationSupplyFormFieldsProps) => {
  const travelDestinationController = useController({
    control,
    defaultValue: initialValues.travelDestination,
    name: 'travelDestination',
    rules: { required: 'Travel destination is required' },
  });
  const startDateController = useController({
    control,
    defaultValue: initialValues.startDate,
    name: 'startDate',
    rules: {
      required: 'Start date is required',
      validate: (value: string) => validateStartDate(value, earliestAvailableDate),
    },
  });
  const returnDateController = useController({
    control,
    defaultValue: initialValues.returnDate,
    name: 'returnDate',
    rules: {
      required: 'Return date is required',
      validate: (value: string) => validateReturnDate(value, earliestAvailableDate),
    },
  });

  return (
    <>
      <InputText
        accessibilityLabel="Travel destination"
        error={travelDestinationController.fieldState.error?.message}
        key="travelDestination"
        label="Travel destination"
        leftIconProps={{ name: 'mapmarker-small' }}
        maxLength={150}
        onChangeText={travelDestinationController.field.onChange}
        placeholder="City, Country"
        required
        returnKeyType="default"
        value={travelDestinationController.field.value}
      />
      <LgSpacing />
      <InputDate
        accessibilityLabel="Start date"
        error={startDateController.fieldState.error?.message}
        key="startDate"
        label="Start date"
        onChangeText={startDateController.field.onChange}
        required
        value={startDateController.field.value}
      />
      <LgSpacing />
      <InputDate
        accessibilityLabel="Return date"
        error={returnDateController.fieldState.error?.message}
        key="returnDate"
        onChangeText={returnDateController.field.onChange}
        label="Return date"
        required
        value={returnDateController.field.value}
      />
      {description ? (
        <XlPadding
          leftPadding={SPACING.STATIC.NONE}
          rightPadding={SPACING.STATIC.NONE}
        >
          <Body>{description}</Body>
        </XlPadding>
      ) : (
        <LgSpacing />
      )}
    </>
  );
};
